/**
 * TipSelector - 팁 선택 컴포넌트
 *
 * 5%, 10%, 15%, 20%, 직접 입력 옵션 제공.
 * selectedTip=0 이면 미선택 상태.
 * 직접 입력은 모달로 퍼센트(%) 또는 금액(페소) 중 선택 가능.
 */
import { useState } from 'react';
import { CustomTipModal } from '@/components/payment/CustomTipModal';

const TIP_OPTIONS = [5, 10, 15, 20] as const;

interface TipSelectorProps {
  selectedTip: number;
  customTip: string;
  customTipMode: 'percentage' | 'amount';
  onSelectTip: (pct: number) => void;
  onCustomTip: (value: string, mode: 'percentage' | 'amount') => void;
  tipAmount: number;
  label: string;
  customLabel: string;
  tipAmountLabel: string;
  formatCurrency: (n: number) => string;
  calculateSubtotal: () => number;
  modalT: {
    enterTip: string;
    percentage: string;
    amount: string;
    enterTipPercentage: string;
    enterTipAmount: string;
    estimatedTip: string;
    cancel: string;
    apply: string;
  };
}

export function TipSelector({
  selectedTip,
  customTip,
  customTipMode,
  onSelectTip,
  onCustomTip,
  tipAmount,
  label,
  customLabel,
  tipAmountLabel,
  formatCurrency,
  calculateSubtotal,
  modalT,
}: TipSelectorProps) {
  const [showCustomTipModal, setShowCustomTipModal] = useState(false);

  const handleTipClick = (pct: number) => {
    if (selectedTip === pct && !customTip) {
      onSelectTip(0);
    } else {
      onSelectTip(pct);
      onCustomTip('', 'percentage');
    }
  };

  const handleCustomClick = () => {
    setShowCustomTipModal(true);
  };

  const handleApplyCustomTip = (tip: string, mode: 'percentage' | 'amount') => {
    onCustomTip(tip, mode);
    onSelectTip(0);
    setShowCustomTipModal(false);
  };

  return (
    <div>
      <p className="text-[14px] font-semibold text-foreground mb-[12px]">{label}</p>
      <div className="grid grid-cols-5 gap-2">
        {TIP_OPTIONS.map((pct) => (
          <button
            key={pct}
            onClick={() => handleTipClick(pct)}
            className={`h-[50px] flex items-center justify-center text-sm font-medium transition-all duration-200 border-2 rounded-[5px] active:scale-95 ${
              selectedTip === pct && !customTip
                ? 'bg-primary border-primary text-white shadow-sm'
                : 'bg-background border-border text-foreground hover:border-primary hover:bg-primary/5'
            }`}
          >
            {pct}%
          </button>
        ))}
        <button
          onClick={handleCustomClick}
          className={`h-[50px] px-3 flex items-center justify-center text-sm font-medium transition-all duration-200 border-2 rounded-[5px] whitespace-nowrap active:scale-95 ${
            customTip
              ? 'bg-primary border-primary text-white shadow-sm'
              : 'bg-background border-border text-foreground hover:border-primary hover:bg-primary/5'
          }`}
        >
          {customLabel}
        </button>
      </div>

      {/* 직접 입력한 팁 표시 */}
      {customTip && !selectedTip && (
        <div className="mt-3 p-3 bg-primary/10 rounded-lg border border-primary/30">
          <p className="text-sm text-foreground text-center">
            {customTipMode === 'percentage' ? `${customTip}%` : formatCurrency(parseFloat(customTip) || 0)}
          </p>
        </div>
      )}

      {tipAmount > 0 && (
        <p className="mt-[8px] text-[13px] text-muted-foreground text-right">
          {tipAmountLabel}: <span className="font-semibold text-foreground">{formatCurrency(tipAmount)}</span>
        </p>
      )}

      <CustomTipModal
        isOpen={showCustomTipModal}
        onClose={() => setShowCustomTipModal(false)}
        onApply={handleApplyCustomTip}
        initialTip={customTip}
        initialMode={customTipMode}
        formatCurrency={formatCurrency}
        calculateSubtotal={calculateSubtotal}
        t={modalT}
      />
    </div>
  );
}
